import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { findFlowSlotForControlExpandId } from '@/helpers/flow-control-playing-segment.helper';
import {
  FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID,
  FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID,
} from '@/components/flows/detail/slot-list/flow-slot-assets-inline-ids';

export type ScheduleLiveTimedSlotLock = {
  /** Inclusive index of the last locked timed slot; -1 when nothing is locked. */
  lastLockedTimedIndex: number;
  lockedTimedSlotIds: ReadonlySet<string>;
};

const EMPTY_LOCK: ScheduleLiveTimedSlotLock = {
  lastLockedTimedIndex: -1,
  lockedTimedSlotIds: new Set(),
};

/**
 * Maps the control-tab "currently playing" expand id to a timed-slot lock prefix on the schedule tab.
 * Slots at or before the playing timed slot cannot be dragged; drops into that prefix are blocked.
 */
export function resolveScheduleLiveTimedSlotLock(
  timedSlots: FlowSlotDto[],
  playingExpandId: string | null | undefined,
): ScheduleLiveTimedSlotLock {
  if (!playingExpandId || timedSlots.length === 0) {
    return EMPTY_LOCK;
  }

  if (
    playingExpandId === FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID ||
    playingExpandId === FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID
  ) {
    return {
      lastLockedTimedIndex: timedSlots.length - 1,
      lockedTimedSlotIds: new Set(timedSlots.map((slot) => slot.id)),
    };
  }

  if (
    playingExpandId === FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID ||
    playingExpandId === FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID
  ) {
    return EMPTY_LOCK;
  }

  const playingSlot = findFlowSlotForControlExpandId(timedSlots, playingExpandId);
  if (!playingSlot) {
    return EMPTY_LOCK;
  }

  const playingIndex = timedSlots.findIndex((slot) => slot.id === playingSlot.id);
  if (playingIndex < 0) {
    return EMPTY_LOCK;
  }

  return {
    lastLockedTimedIndex: playingIndex,
    lockedTimedSlotIds: new Set(timedSlots.slice(0, playingIndex + 1).map((slot) => slot.id)),
  };
}

export function isScheduleLiveTimedSlotLocked(slotId: string, lock: ScheduleLiveTimedSlotLock | undefined): boolean {
  return Boolean(lock?.lockedTimedSlotIds.has(slotId));
}

/** First timed-list index where a slot may be inserted during live (0 when nothing is locked). */
export function minLiveTimedDropIndex(lock: ScheduleLiveTimedSlotLock | undefined): number {
  if (!lock || lock.lastLockedTimedIndex < 0) {
    return 0;
  }
  return lock.lastLockedTimedIndex + 1;
}

export function isLiveTimedDropIndexAllowed(dropIndex: number, lock: ScheduleLiveTimedSlotLock | undefined): boolean {
  return dropIndex >= minLiveTimedDropIndex(lock);
}

/**
 * Locked timed slots must stay a contiguous prefix (canonical order). Unlocked slots cannot sit above them.
 */
export function isTimedOrderLockedPrefixValid(orderedIds: string[], lockedIds: ReadonlySet<string>): boolean {
  if (lockedIds.size === 0) {
    return true;
  }

  let prefixEnded = false;
  for (const id of orderedIds) {
    const isLocked = lockedIds.has(id);
    if (prefixEnded && isLocked) {
      return false;
    }
    if (!isLocked) {
      prefixEnded = true;
    }
  }
  return true;
}

export function isTimedReorderMoveAllowed(
  activeId: string,
  nextTimedIds: string[],
  lock: ScheduleLiveTimedSlotLock | undefined,
): boolean {
  if (isScheduleLiveTimedSlotLocked(activeId, lock)) {
    return false;
  }
  return isTimedOrderLockedPrefixValid(nextTimedIds, lock?.lockedTimedSlotIds ?? new Set());
}
